import type { getOneOrderOutput } from "@/api/custom_types";
import RelationNameLink from "@/pages/relations/RelationNameLink";
import { formatNumber, getOrderEmail, renderAuthData } from "@/utils/tools";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Icon,
	Typography,
} from "@st-marketplace/ui";
import { t } from "i18next";

interface OrderDetailsInfoDetailProps {
	order: getOneOrderOutput;
}
function OrderDetailsInfoDetail({ order }: OrderDetailsInfoDetailProps) {
	// a customer-link order not claimed yet has no EA account
	const account = order.schema_type === "end_customer_unclaimed" ? null : order;
	const details: { id: number; label: string; value: string | null }[] = [
		{
			id: 1,
			label: t("orderInfo.info.cardInfoEmail"),
			value: getOrderEmail(order),
		},
		{
			id: 2,
			label: t("orderInfo.info.cardInfoBuyer"),
			value: order.buyer_name,
		},
		{
			id: 3,
			label: t("orderInfo.info.cardInfoSeller"),
			value: order.seller_name,
		},
		{
			id: 4,
			label: t("orderInfo.info.cardInfoAmount"),
			value: formatNumber(order.amount),
		},
		{
			id: 5,
			label:
				"auth" in order && order.auth?.type === "backup_code"
					? t("orderInfo.info.cardInfoBackupCode")
					: t("orderInfo.info.cardInfoGauth"),
			value: "auth" in order ? renderAuthData(order.auth) : "",
		},
		{
			id: 6,
			label: t("orderInfo.info.cardInfoUnitPrice"),
			value: formatNumber(order.unit_price, "price"),
		},
		{
			id: 7,
			label: t("orderInfo.info.cardInfoTotalPrice"),
			value: formatNumber(order.total_price, "price"),
		},
	];

	// without an account there are no balances or gamer tag: hide the rows instead of showing 0
	if (account) {
		details.push(
			{
				id: 8,
				label: t("orderInfo.info.cardInfoStartingCoins"),
				value: formatNumber(account.starting_balance),
			},
			{
				id: 9,
				label: t("orderInfo.info.cardInfoEndingCoins"),
				value: formatNumber(account.ending_balance),
			},
			{
				id: 10,
				label: t("orderInfo.info.cardInfoGamerTag"),
				value: account.gamer_tag,
			},
		);
	}

	if (order.schema_type === "for_admin") {
		details.push({
			id: 11,
			label: t("orderInfo.info.cardInfoPassword"),
			value: order.ea_password,
		});
	}

	if (order.schema_type === "for_admin" && order.proxy) {
		details.push(
			{
				id: 12,
				label: t("orderInfo.info.cardInfoProxyIP"),
				value: order.proxy.ip_address,
			},
			{
				id: 13,
				label: t("orderInfo.info.cardInfoProxyPort"),
				value: order.proxy.port.toString(),
			},
			{
				id: 14,
				label: t("orderInfo.info.cardInfoProxyUsername"),
				value: order.proxy.username,
			},
			{
				id: 15,
				label: t("orderInfo.info.cardInfoProxyPassword"),
				value: order.proxy.password,
			},
		);
	}

	return (
		<Card className="border-secondary-border">
			<CardHeader>
				<div className="flex items-center gap-3">
					<Icon name="information-line" className="text-primary-100" />
					<CardTitle>
						<Typography variant="title-sm">
							{t("orderInfo.info.cardInfoTitle").toUpperCase()}
						</Typography>
					</CardTitle>
				</div>
			</CardHeader>
			<CardContent className="ms-0 flex flex-col gap-5 lg:ms-9">
				{details.map(({ id, label, value }) => {
					if (
						id === 5 &&
						(!("auth" in order) || order.auth?.type === "cookie")
					) {
						return null;
					}
					return (
						<div key={id} className="flex">
							<Typography variant="body-sm" className="w-[35%]">
								{label}
							</Typography>
							<Typography
								variant="body-sm"
								className="w-[65%] truncate text-secondary-text-100"
							>
								{id === 2 || id === 3 ? (
									<RelationNameLink
										name={String(value ?? "")}
										relationId={order.relation_id}
										party={id === 2 ? "buyer" : "seller"}
									/>
								) : (
									value
								)}
							</Typography>
						</div>
					);
				})}
			</CardContent>
		</Card>
	);
}

export default OrderDetailsInfoDetail;
